import React, { useState, useContext, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppBarMenu from "../../components/AppBarMenu";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import ResourceList from "../../components/shared/ResourceList";
import EngineerDrawer from "../../components/management/EngineerDrawer";
import { ApiDataContext } from "../../contexts/ApiDataContext";import { formatCPF, formatPhone } from "../../utils/formatters";
/**
 * Página dedicada para gestão de engenheiros
 * Usa ResourceList para listagem e EngineerDrawer para criar/editar
 */
export default function EngineersPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiData } = useContext(ApiDataContext);
  
  // Estado do drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedEngineerId, setSelectedEngineerId] = useState(null);
  const [createdEngineerId, setCreatedEngineerId] = useState(null);

  // Função de busca client-side (engenheiros vêm do ApiDataContext)
  const fetchEngineers = useCallback(async ({ page, limit, search }) => {
    const engineers = apiData.engineers || [];
    
    // Filtrar por search
    let filtered = engineers;
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = engineers.filter(eng => 
        eng.name?.toLowerCase().includes(searchLower) ||
        eng.email?.toLowerCase().includes(searchLower) ||
        eng.cpf?.includes(search)
      );
    }

    // Paginar client-side (page começa em 1 no ResourceList)
    const start = (page - 1) * limit;
    const end = start + limit;
    const paginated = filtered.slice(start, end).map(eng => ({
      ...eng,
      _id: eng.id // ResourceList espera _id
    }));

    return {
      data: paginated,
      pagination: {
        total: filtered.length,
        page,
        limit
      }
    };
  }, [apiData.engineers]);

  // Colunas da tabela
  const columns = [
    { 
      header: "Nome", 
      accessor: "name",
      align: "left"
    },
    { 
      header: "Email", 
      accessor: "email",
      align: "left"
    },
    { 
      header: "CPF", 
      accessor: "cpf",
      align: "left"
    },
    { 
      header: "Telefone", 
      accessor: "phone",
      align: "left"
    },
  ];

  const handleCreateEngineer = () => {
    setDrawerMode("create");
    setSelectedEngineerId(null);
    setDrawerOpen(true);
  };

  const handleRowClick = (engineer) => {
    setDrawerMode("edit");
    setSelectedEngineerId(engineer.id);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode("create");
    setSelectedEngineerId(null);
    
    // Se criou um engenheiro, aguarda e fecha
    if (createdEngineerId) {
      setTimeout(() => {
        setCreatedEngineerId(null);
      }, 1000);
    }
  };

  return (
    <AppBarMenu>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <h1 style={{ margin: 0 }}>Engenheiros</h1>
            <p style={{ margin: "8px 0 0 0", color: "text.secondary" }}>
              Gerencie os engenheiros cadastrados no sistema
            </p>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateEngineer}
            sx={{ height: 40 }}
          >
            Novo Engenheiro
          </Button>
        </Box>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <ResourceList
            fetchData={fetchEngineers}
            columns={columns}
            searchParam="search"
            onRowClick={handleRowClick}
            storageKey="engineers"
            resourceName="Engenheiros"
            initialSearch={searchParams.get('search') || ''}
          />
        </Paper>

        <EngineerDrawer
          open={drawerOpen}
          onClose={handleCloseDrawer}
          mode={drawerMode}
          engineerId={selectedEngineerId}
          onEngineerCreated={(engineerId) => setCreatedEngineerId(engineerId)}
        />
      </Container>
    </AppBarMenu>
  );
}
